
import './App.css';
import _ from "lodash";
import axios from 'axios';
import pLimit from 'p-limit';
import {Mutex} from 'async-mutex';

import { useState, useReducer } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import Button from '@mui/material/Button'

const languages = [
  "Abnaki",
  "Afrikaans",
  "Akkadian",
  "Algonquian",
  "American English",
  "American Spanish",
  "Anglian",
  "Anglo-French",
  "Anglo-Latin",
  "Anglo-Norman",
  "Arabic",
  "Aramaic",
  "Arawakan",
  "Armenian",
  "Assyrian",
  "Attic",
  "Basque",
  "Breton",
  "Cantonese",
  "Carib",
  "Catalan",
  "Celtic",
  "Cherokee",
  "Chinook",
  "Church Latin",
  "Coptic",
  "Cornish",
  "Croatian",
  "Czech",
  "Danish",
  "Dravidian",
  "Dutch",
  "Ecclesiastical Greek",
  "East Frisian",
  "Egyptian",
  "English",
  "Estonian",
  "Etruscan",
  "Faroese",
  "Finnish",
  "Flemish",
  "Frankish",
  "French",
  "Frisian",
  "Fulani",
  "Gallo-Romance",
  "Gallo-Roman",
  "Gaelic",
  "Gaulish",
  "German",
  "Germanic",
  "Gothic",
  "Greek",
  "Guarani",
  "Hawaiian",
  "Hebrew",
  "Hungarian",
  "Ibo",
  "Irish",
  "Iranian",
  "Iroquoian",
  "Italian",
  "Japanese",
  "Kentish",
  "Kurdish",
  "Kwa",
  "Late Latin",
  "Latin",
  "Lithuanian",
  "Low German",
  "Malay",
  "Mandarin",
  "Mandingo",
  "Medieval Latin",
  "Mercian",
  "Mexican Spanish",
  "Micmac",
  "Middle Dutch",
  "Middle English",
  "Middle French",
  "Middle High German",
  "Middle Irish",
  "Middle Low German",
  "Modern English",
  "Modern Greek",
  "Muskogean",
  "Nahuatl",
  "North Germanic",
  "North Sea Germanic",
  "Northumbrian",
  "Old Celtic",
  "Old French",
  "Ojibwa",
  "Old Church Slavonic",
  "Old Danish",
  "Old Dutch",
  "Old English",
  "Old Frisian",
  "Old High German",
  "Old Irish",
  "Old Italian",
  "Old Low German",
  "Old Norse",
  "Old North French",
  "Old Persian",
  "Old Provençal",
  "Old Prussian",
  "Old Saxon",
  "Old Slavic",
  "Old Spanish",
  "Old Swedish",
  "Oscan",
  "Pashto",
  "Pennsylvania Dutch",
  "Persian",
  "Phoenician",
  "Phyrgian",
  "PIE",
  "Piman",
  "Polish",
  "Portuguese",
  "Pre-Greek",
  "Proto-Germanic",
  "Proto-Italic",
  "Proto-Indo-European",
  "Quechua",
  "Russian",
  "Sanskrit",
  "Scandinavian",
  "Scottish",
  "Semitic",
  "Serbian",
  "Serbo-Croatian",
  "Sinhalese",
  "Siouan",
  "Slavic",
  "Slovak",
  "Spanish",
  "Sumerian",
  "Swedish",
  "Tagalog",
  "Tamil",
  "Telugu",
  "Thai",
  "Tibetan",
  "Tupi",
  "Turkish",
  "Turkic",
  "Twi",
  "Ukrainian",
  "Uto-Aztecan",
  "Vulgar Latin",
  "West African",
  "West Frisian",
  "West Germanic",
  "Wolof",
  "West Saxon",
  "Xhosa",
  "Yoruba",
]

function getLanguageColor(language) {
  if (!language || language === 'Unknown') return '#cccccc';
  
  // Create a consistent hash for the language name
  let hash = 0;
  for (let i = 0; i < language.length; i++) {
    hash = language.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate HSL color with constrained lightness and saturation
  const hue = Math.abs(hash) % 360; // Full hue range
  const saturation = 70 + Math.abs(hash) % 30; // 70-100% saturation
  const lightness = 75 + Math.abs(hash) % 15; // 75-90% lightness
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

class WordLookupManager {
  constructor() {
    this.inFlightMap = {}; // Map to store promises for each word
    this.batchSize = 15; // Batch size for backend requests
    this.concurrencyLimit = 3; // Maximum number of concurrent requests
    this.limit = pLimit(this.concurrencyLimit); // Concurrency limiter
  }

  deduplicateAndAddToInFlightMap(stringArr) {
    let deduped = []
    for (let word of stringArr) {
        if (!this.inFlightMap[word]) {
            deduped.push(word);
            this.inFlightMap[word] = true;
        }
    }
    return deduped;
  }

  removeFromFlight(words) {
    for (let word of words) {
      this.inFlightMap[word] = false
    }
  }

  addWords(words, lookupFn) {
    const newWords = this.deduplicateAndAddToInFlightMap(words)
    const batches = [];
    for (let i = 0; i < newWords.length; i += this.batchSize) {
      batches.push(newWords.slice(i, i + this.batchSize)); // Split new words into batches
    }
    // console.log("look mama I batched it!", batches)
    const promises = batches.map(batch => {
      return this.limit(() => lookupFn(batch)); // Call the provided lookup function with each batch
    });
    return Promise.all(promises).then(results => results.flat()); // Flatten the array of results
  }
}

const wordLookupManager = new WordLookupManager();


function splitIntoWords(text) {
  let words = text.split(" ");
  words = _.map(words, word => {
    return word.replace(/[^0-9a-z]/gi, '').toLowerCase();
  });
  return _.filter(words, word => word.length > 0);
}

function wordsToLanguageFrequency(words, wordToLanguageMap, countDuplicates) {
  let deduped = []
  if (countDuplicates) {
    let wordMap = {}
    for (let word of words) {
        if (!wordMap[word]) {
            deduped.push(word);
            wordMap[word] = true;
        }
    }
  } else {
    deduped = words
  }
  
  return _.reduce(_.map(deduped, word => {
    let language = wordToLanguageMap[word];
    if (language == undefined) {
      language = 'Unknown';
    }
    return language
  }), (freqMap, language) => {
    if (freqMap[language]) {
      freqMap[language]++;
    } else {
      freqMap[language] = 1;
    }
    return freqMap;
  } , {});
}

function languageMapReducer(wordToLanguageMap, action) {
  switch (action.type) {
    case 'update': 
      const newWordMap = action.newWordMap;
      console.log("adding " + Object.keys(action.newWordMap).length + " keys to existing " + Object.keys(wordToLanguageMap).length)
      for (let word in wordToLanguageMap) {
        newWordMap[word] = wordToLanguageMap[word]
      }
      return newWordMap;
    default:
      throw Error('Unknown action: ' + action.type);
  }
}

const WordHighlighter = ({ text, wordToLanguageMap }) => {
  // Split the text into words while preserving punctuation and whitespace
  const wordsWithPunctuation = text.split(/(\s+)/);
  
  return (
    <div style={{ 
      textAlign: 'left', 
      padding: '20px', 
      fontSize: '18px', 
      lineHeight: '1.6',
      backgroundColor: '#f8f8f8',
      borderRadius: '8px',
      margin: '20px 0'
    }}>
      {wordsWithPunctuation.map((word, index) => {
        // Clean the word for lookup (remove punctuation and lowercase)
        const cleanWord = word.replace(/[^0-9a-z]/gi, '').toLowerCase();
        const language = wordToLanguageMap[cleanWord] || 'Unknown';
        const color = getLanguageColor(language);
        
        // Don't highlight whitespace or empty strings
        if (!cleanWord || !cleanWord.trim()) {
          return (<span key={index}>{word}</span>);
        }
        
        return (
          <span 
            key={index}
            style={{
              backgroundColor: color,
              padding: '2px 4px',
              borderRadius: '4px',
              margin: '0 1px',
              position: 'relative'
            }}
            title={`${cleanWord}: ${language}`}
          >
            {word}
          </span>
        );
      })}
    </div>
  );
};

function App() {
  const [rawText, setRawText] = useState("Type a sentence and discover its etymology");
  const [countDuplicates, setCountDuplicates] = useState(false);
  //const [wordToLanguageMap, setWordToLanguageMap] = useState({});
  const [wordToLanguageMap, dispatch] = useReducer(languageMapReducer, {})
  // a write Mutex for wordToLanguageMap
  // const mutex = new Mutex();

  function lookupAgainstBackend(words){
    const data = {
      words
    }
    console.log("posting with data!", data)
    return axios.post("https://pkuj506kcg.execute-api.us-west-2.amazonaws.com/default/etymologyLookup", data)
    .then((result) => {
      console.log("got result data", result.data)
      if (result && result.data && result.data.words && Object.keys(result.data.words).length != 0) {
        wordLookupManager.removeFromFlight(Object.keys(result.data.words))
        dispatch({
          type: 'update',
          newWordMap: result.data.words
        })
      }
    }).catch(err => {
      console.log("Attempted on words", JSON.stringify(words))
      console.error("Failed to update words from backend:", err)
    });
  }

  let words = splitIntoWords(rawText);

  let wordsNotInMap = words.filter(word => !wordToLanguageMap[word]);

  console.log("words NOT in map: ", wordsNotInMap.length)
  if (wordsNotInMap.length != 0) {
    console.log("words in language map: ", Object.keys(wordToLanguageMap).length)
    wordLookupManager.addWords(wordsNotInMap, lookupAgainstBackend);
  }

  
  let languageFrequencyMap = wordsToLanguageFrequency(words, wordToLanguageMap, countDuplicates);
  return (
    <div className="App">
      <header className="App-header">
        <textarea value={rawText} onChange={(e) => setRawText(e.target.value)}/>
        {/*<Button variant="contained" onClick={()=>lookupAgainstBackend()}>EtymAnalyze!</Button>*/}
        <WordHighlighter text={rawText} wordToLanguageMap={wordToLanguageMap} />
        <PieChart
          series={[
            {
              data: _.map(_.keys(languageFrequencyMap).filter(lang => lang != true || 'Unknown'), lang => {
                return {label: lang, value: languageFrequencyMap[lang], id: languages.indexOf(lang), color: getLanguageColor(lang)}
              }),
            }
          ]}
          width={400}
        />
      </header>
    </div>
  );
}

export default App;
